/*
 * Configuration des trois langues du site.
 * Le français reste la langue par défaut et conserve ses adresses actuelles ;
 * l'anglais et l'espagnol vivent sous /en/ et /es/ avec des adresses traduites.
 */

export const LANGS = ["fr", "en", "es"];
export const DEFAULT_LANG = "fr";

export const LANG_LABELS = { fr: "FR", en: "EN", es: "ES" };
export const HTML_LANG = { fr: "fr-FR", en: "en", es: "es" };
export const OG_LOCALE = { fr: "fr_FR", en: "en_GB", es: "es_ES" };

/** Rubriques principales : clé interne → segment d'URL par langue. */
export const PAGE_SLUGS = {
    home: { fr: "", en: "", es: "" },
    tarifs: { fr: "tarifs", en: "pricing", es: "tarifas" },
    medecin: { fr: "le-medecin", en: "the-doctor", es: "la-doctora" },
    cabinet: { fr: "le-cabinet-asnieres", en: "the-clinic-asnieres", es: "la-clinica-asnieres" },
    conseils: {
        fr: "conseils-avant-apres-soin",
        en: "before-and-after-care",
        es: "consejos-antes-y-despues",
    },
    notfound: { fr: "introuvable", en: "not-found", es: "no-encontrado" },
};

/** Les mentions légales restent uniquement en français (décision de la cliente). */
export const LEGAL_PATH = "/mentions-legales";

/** Pages de soins : clé canonique (= testids) → slug par langue. */
export const SOIN_SLUGS = {
    sopk: { fr: "sopk", en: "pcos", es: "sop" },
    menopause: { fr: "menopause", en: "menopause", es: "menopausia" },
    "epilation-laser": {
        fr: "epilation-laser",
        en: "laser-hair-removal",
        es: "depilacion-laser",
    },
    melasma: { fr: "melasma", en: "melasma", es: "melasma" },
    "chute-de-cheveux": {
        fr: "chute-de-cheveux",
        en: "hair-loss",
        es: "caida-del-cabello",
    },
    "acide-hyaluronique": {
        fr: "acide-hyaluronique",
        en: "hyaluronic-acid",
        es: "acido-hialuronico",
    },
    "peau-et-rides": { fr: "peau-et-rides", en: "skin-and-wrinkles", es: "piel-y-arrugas" },
    "sante-intime": { fr: "sante-intime", en: "intimate-health", es: "salud-intima" },
    acne: { fr: "acne", en: "adult-acne", es: "acne-adulto" },
};

export const SOIN_KEYS = Object.keys(SOIN_SLUGS);

/** Langue déduite du chemin : /en/... → en, /es/... → es, sinon fr. */
export function langFromPath(pathname) {
    const first = pathname.split("/")[1];
    return LANGS.includes(first) && first !== DEFAULT_LANG ? first : DEFAULT_LANG;
}

const withPrefix = (lang, segment) => {
    const prefix = lang === DEFAULT_LANG ? "" : `/${lang}`;
    if (!segment) return prefix || "/";
    return `${prefix}/${segment}`;
};

/** Adresse d'une rubrique principale dans une langue donnée. */
export function pagePath(lang, key) {
    return withPrefix(lang, PAGE_SLUGS[key][lang]);
}

/** Adresse d'une page de soin dans une langue donnée. */
export function soinPath(lang, key) {
    return withPrefix(lang, SOIN_SLUGS[key][lang]);
}

/** Clé canonique d'un soin à partir du slug rencontré dans l'URL. */
export function soinKeyFromSlug(lang, slug) {
    return SOIN_KEYS.find((key) => SOIN_SLUGS[key][lang] === slug) || null;
}

/** Même page, autre langue — utilisé par le sélecteur de langue. */
export function equivalentPath(pathname, targetLang) {
    const lang = langFromPath(pathname);
    const segment = pathname
        .replace(/\/+$/, "")
        .split("/")
        .filter(Boolean)
        .filter((part) => part !== lang || lang === DEFAULT_LANG)
        .join("/");

    if (!segment) return pagePath(targetLang, "home");
    if (`/${segment}` === LEGAL_PATH) return LEGAL_PATH;

    const pageKey = Object.keys(PAGE_SLUGS).find((key) => PAGE_SLUGS[key][lang] === segment);
    if (pageKey) return pagePath(targetLang, pageKey);

    const soinKey = soinKeyFromSlug(lang, segment);
    if (soinKey) return soinPath(targetLang, soinKey);

    return pagePath(targetLang, "home");
}

/** Toutes les adresses d'une même page, pour les balises hreflang. */
export function alternatesFor({ pageKey, soinKey }) {
    const build = (lang) => (soinKey ? soinPath(lang, soinKey) : pagePath(lang, pageKey));
    return LANGS.reduce((acc, lang) => ({ ...acc, [lang]: build(lang) }), {});
}
